import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Layout } from "react-grid-layout";
import { v4 as uuidv4 } from "uuid";
import { Dashboard, DashboardItem } from "../../pages/Dashboard/Dashboard.types";
import { crmApi } from "../../api/crmApi";

type DashboardStatus = "idle" | "loading" | "saving" | "succeeded" | "failed";

interface DashboardState extends Dashboard {
    status: DashboardStatus;
    error: string | null;
    snapshot: DashboardItem[] | null; // pre-edit snapshot for cancel
}

const initialState: DashboardState = {
    username: "",
    dashboardId: "",
    dashboardItem: [],
    status: "idle",
    error: null,
    snapshot: null,
};

export const fetchDashboard = createAsyncThunk("dashboard/fetch", async () => {
    return await crmApi.getBootstrap();
});

export const saveDashboard = createAsyncThunk("dashboard/save", async (_, { getState }) => {
    const state = getState() as { dashboard: DashboardState };
    return await crmApi.updateBootstrap({
        dashboardItems: state.dashboard.dashboardItem as unknown[],
    });
});

const dashboardSlice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {
        snapshotForEdit: (state) => {
            state.snapshot = JSON.parse(JSON.stringify(state.dashboardItem));
        },
        cancelEdit: (state) => {
            if (state.snapshot !== null) {
                state.dashboardItem = state.snapshot;
            }
            state.snapshot = null;
        },
        addDashboardItem: (state, action: PayloadAction<Partial<DashboardItem>>) => {
            const newItemId = uuidv4();
            const defaultSize = { w: 6, h: 4 };

            const newLayoutItem: Layout = {
                i: newItemId,
                x: (state.dashboardItem.length * 2) % 12,
                y: Math.floor(state.dashboardItem.length / 6) * 4,
                w: defaultSize.w,
                h: defaultSize.h,
            };
            const newItem: DashboardItem = {
                title: action.payload.title ?? "",
                type: action.payload.type ?? "NUMBER_CARD",
                dataId: action.payload.dataId ?? "",
                hideTitle: action.payload.hideTitle ?? false,
                format: action.payload.format ?? null,
                caption: action.payload.caption ?? "",
                textEditorConfig: Array.isArray(action.payload.textEditorConfig)
                    ? action.payload.textEditorConfig
                    : undefined,
                chartType: action.payload.chartType ?? undefined,
                id: newItemId,
                layout: { ...newLayoutItem },
            };
            state.dashboardItem.push(newItem);
        },
        updateDashboardItem: (state, action) => {
            const selectedItemIndex = state.dashboardItem.findIndex(
                (item: DashboardItem) => item.layout.i === action.payload.id,
            );
            if (selectedItemIndex !== -1) {
                state.dashboardItem[selectedItemIndex] = {
                    ...state.dashboardItem[selectedItemIndex],
                    ...action.payload,
                };
            }
        },
        deleteDashboardItem: (state, action: PayloadAction<string>) => {
            state.dashboardItem = state.dashboardItem.filter(
                (item: DashboardItem) => item.layout.i !== action.payload,
            );
        },
        updateLayouts: (state, action: PayloadAction<Layout[]>) => {
            state.dashboardItem = state.dashboardItem.map((item) => ({
                ...item,
                layout: action.payload.find((l) => l.i === item.layout.i) ?? item.layout,
            }));
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDashboard.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchDashboard.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.dashboardId = action.payload._id;
                state.username = action.payload.userId;
                state.dashboardItem = (action.payload.dashboardItems ?? []) as DashboardItem[];
            })
            .addCase(fetchDashboard.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message ?? "Failed to fetch dashboard";
            })
            .addCase(saveDashboard.pending, (state) => {
                state.status = "saving";
                state.error = null;
            })
            .addCase(saveDashboard.fulfilled, (state) => {
                state.status = "succeeded";
                state.snapshot = null;
            })
            .addCase(saveDashboard.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message ?? "Failed to save dashboard";
            });
    },
});

export const {
    snapshotForEdit,
    cancelEdit,
    addDashboardItem,
    updateDashboardItem,
    deleteDashboardItem,
    updateLayouts,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
